/**
 * Reminders Hook
 *
 * Manages reminders for medications, appointments, and custom events.
 * Reminders trigger push notifications and in-app notifications.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../api/louise-api";
import { throwApiError } from "../api/api-error";
import type { paths } from "@src/packages/openapi/schema";
import type { ResponseBody, RequestBody } from "@src/packages/openapi/helpers";

// ============================================================================
// Types
// ============================================================================

/** Reminder as returned from GET /{tenantId}/me/reminders — derived from the OpenAPI schema. */
export type Reminder = ResponseBody<
  paths["/{tenantId}/me/reminders"]["get"]
>["data"][number];

export type ReminderType = Reminder["type"];
export type ReminderFrequency = Reminder["frequency"];
export type ReminderStatus = Reminder["status"];

/** Weekly time entry for reminder scheduling. */
export interface WeeklyTime {
  day: string;
  time: string;
}

export type CreateReminderPayload = NonNullable<
  RequestBody<paths["/{tenantId}/me/reminders"]["post"]>
>;
export type UpdateReminderPayload = NonNullable<
  RequestBody<paths["/{tenantId}/me/reminders/{id}"]["patch"]>
>;

// ============================================================================
// Query Keys
// ============================================================================

export const reminderKeys = {
  all: ["reminders"] as const,
  list: (tenantId: string, status?: ReminderStatus) =>
    [...reminderKeys.all, "list", tenantId, status] as const,
  detail: (tenantId: string, id: string) =>
    [...reminderKeys.all, "detail", tenantId, id] as const,
};

// ============================================================================
// API Fetchers
// ============================================================================

async function fetchReminders(
  client: ReturnType<typeof useApiClient>["client"],
  tenantId: string,
  status?: ReminderStatus,
): Promise<Reminder[]> {
  const response = await client.GET("/{tenantId}/me/reminders", {
    params: {
      path: { tenantId },
    },
  });

  if (response.error) {
    throwApiError(
      response.error,
      "Failed to load reminders",
      "useReminders",
      response.response?.status,
    );
  }

  return response.data?.data ?? [];
}

// ============================================================================
// Hooks
// ============================================================================

export interface UseRemindersOptions {
  status?: ReminderStatus;
  enabled?: boolean;
}

/**
 * Hook to fetch reminders.
 */
export function useReminders(options: UseRemindersOptions = {}) {
  const { status, enabled = true } = options;
  const { client, tenantId } = useApiClient();

  return useQuery({
    queryKey: reminderKeys.list(tenantId ?? "", status),
    queryFn: () => fetchReminders(client, tenantId!, status),
    enabled: enabled && !!tenantId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to create a reminder.
 */
export function useCreateReminder() {
  const { client, tenantId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateReminderPayload) => {
      if (!tenantId) {
        throw new Error("No tenant ID");
      }

      const response = await client.POST("/{tenantId}/me/reminders", {
        params: { path: { tenantId } },
        body: {
          ...payload,
          timezone:
            payload.timezone ??
            Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });

      if (response.error) {
        throwApiError(
          response.error,
          "Failed to create reminder",
          "useCreateReminder",
          response.response?.status,
        );
      }

      return response.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reminderKeys.all });
    },
  });
}

/**
 * Hook to update a reminder.
 */
export function useUpdateReminder() {
  const { client, tenantId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      reminderId,
      ...payload
    }: UpdateReminderPayload & { reminderId: string }) => {
      if (!tenantId) {
        throw new Error("No tenant ID");
      }

      const response = await client.PATCH("/{tenantId}/me/reminders/{id}", {
        params: { path: { tenantId, id: reminderId } },
        body: payload,
      });

      if (response.error) {
        throwApiError(
          response.error,
          "Failed to update reminder",
          "useUpdateReminder",
          response.response?.status,
        );
      }

      return response.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reminderKeys.all });
    },
  });
}

/**
 * Hook to delete a reminder.
 */
export function useDeleteReminder() {
  const { client, tenantId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reminderId: string) => {
      if (!tenantId) {
        throw new Error("No tenant ID");
      }

      const response = await client.DELETE("/{tenantId}/me/reminders/{id}", {
        params: { path: { tenantId, id: reminderId } },
      });

      if (response.error) {
        throwApiError(
          response.error,
          "Failed to delete reminder",
          "useDeleteReminder",
          response.response?.status,
        );
      }

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reminderKeys.all });
    },
  });
}

/**
 * Hook to pause/resume a reminder.
 */
export function usePauseReminder() {
  const updateReminder = useUpdateReminder();

  return useMutation({
    mutationFn: async ({
      reminderId,
      paused,
    }: {
      reminderId: string;
      paused: boolean;
    }) => {
      return updateReminder.mutateAsync({
        reminderId,
        status: paused ? "paused" : "active",
      });
    },
  });
}

// ============================================================================
// Helper: Create medication reminder
// ============================================================================

export interface CreateMedicationReminderOptions {
  patientMedicationId: string;
  medicationName: string;
  /** Times to take medication (HH:MM format) */
  times: string[];
  /** Start date (YYYY-MM-DD) */
  startDate: string;
  /** End date (YYYY-MM-DD), null for indefinite */
  endDate?: string | null;
  /** Push token for notifications */
  pushToken?: string | null;
}

/**
 * Hook to create a medication reminder with sensible defaults.
 * Uses "daily" frequency - the times array specifies when to remind each day.
 */
export function useCreateMedicationReminder() {
  const createReminder = useCreateReminder();

  return useMutation({
    mutationFn: async (options: CreateMedicationReminderOptions) => {
      return createReminder.mutateAsync({
        type: "medication",
        title: `Time to take ${options.medicationName}`,
        message: `Don't forget to take your ${options.medicationName}`,
        patientMedicationId: options.patientMedicationId,
        medicationName: options.medicationName,
        frequency: "daily",
        times: options.times, // e.g., ["08:00", "14:00", "20:00"]
        startDate: options.startDate,
        endDate: options.endDate ?? undefined,
        pushToken: options.pushToken,
      });
    },
  });
}

// The former useCreateAppointmentReminder helper had zero consumers and was
// superseded by the appointment mutations' reminder sync (#2243) — see
// appointment-reminder-payload.ts. One source of truth: the reminder
// resource, written only by the appointment lifecycle.
